import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from '@/components/common/Button';
import Text from '@/components/common/Text';
import GreyBox from '@/components/common/GreyBox';

import type { ErrorResponse } from '@/types/client.types';
import type {
  EnergyYearlyData,
  EnergyYearData,
  MonthlyValues,
  YearCalculation,
} from '@/types/report/energyUsageAnalysis.types';

import { QUERY_KEY } from '@/constants/queryKey';
import {
  getEnergyUsageAnalysis,
  postEnergyUsageAnalysis,
} from '@/apis/report/energyUsageAnalysis';

import { Theme } from '@/styles/Theme';
import * as S from './style';

// ────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────
const GAS_KCAL = 10290;
const ELEC_KCAL = 2290;
const WATER_KCAL = 1000000;
const TOE_BASE = 10000000;
const KCAL_TO_KWH = 4184 * 2.777778e-7;
const PF_GAS = 1.1;
const PF_WATER = 0.728;
const PF_ELEC = 2.75;

const MONTHS = [
  '1월', '2월', '3월', '4월', '5월', '6월',
  '7월', '8월', '9월', '10월', '11월', '12월',
];

const EMPTY_MONTHLY: MonthlyValues = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

function emptyYear(): EnergyYearData {
  return {
    gas: [...EMPTY_MONTHLY] as MonthlyValues,
    water: [...EMPTY_MONTHLY] as MonthlyValues,
    elec: [...EMPTY_MONTHLY] as MonthlyValues,
  };
}

function getDefaultYears(): string[] {
  const current = new Date().getFullYear();
  return [String(current - 2), String(current - 1), String(current)];
}

function safeSum(arr: number[]): number {
  return arr.reduce((s, v) => s + (Number(v) || 0), 0);
}

function calcYear(data: EnergyYearData, floorArea: number): YearCalculation {
  const gasTotal = safeSum(data.gas);
  const waterTotal = safeSum(data.water);
  const elecTotal = safeSum(data.elec);
  const fa = floorArea || 1;

  const gasToe = (gasTotal * GAS_KCAL) / TOE_BASE;
  const elecToe = (elecTotal * ELEC_KCAL) / TOE_BASE;
  const waterToe = (waterTotal * WATER_KCAL) / TOE_BASE;

  const gasBaseKwh = gasTotal * GAS_KCAL * KCAL_TO_KWH;
  const waterBaseKwh = waterTotal * WATER_KCAL * KCAL_TO_KWH;
  const elecBaseKwh = elecTotal;

  return {
    totals: { gas: gasTotal, water: waterTotal, elec: elecTotal },
    toe: {
      gasToe,
      elecToe,
      waterToe,
      totalToe: gasToe + elecToe + waterToe,
    },
    primaryEnergy: {
      primaryGas: (gasBaseKwh * PF_GAS) / fa,
      primaryWater: (waterBaseKwh * PF_WATER) / fa,
      primaryElec: (elecBaseKwh * PF_ELEC) / fa,
      total:
        (gasBaseKwh * PF_GAS) / fa +
        (waterBaseKwh * PF_WATER) / fa +
        (elecBaseKwh * PF_ELEC) / fa,
    },
  };
}

const EnergyUsageMatrix = () => {
  const params = useParams();
  const inspectionAreaId = Number(params.id);
  const queryClient = useQueryClient();

  const years = useMemo(() => getDefaultYears(), []);

  const [isEdit, setEdit] = useState(false);
  const [yearlyData, setYearlyData] = useState<EnergyYearlyData>({});
  const [floorArea, setFloorArea] = useState(0);
  const [opinion, setOpinion] = useState('');

  const { data: fetchedData } = useQuery({
    queryKey: [QUERY_KEY.REPORT.ENERGY_USAGE_ANALYSIS, inspectionAreaId],
    queryFn: () => getEnergyUsageAnalysis(inspectionAreaId),
  });

  useEffect(() => {
    if (fetchedData?.data) {
      const d = fetchedData.data;
      const yd: EnergyYearlyData = {};
      years.forEach((y) => {
        yd[y] = d.yearly_data?.[y]
          ? {
              gas: [...(d.yearly_data[y].gas || EMPTY_MONTHLY)] as MonthlyValues,
              water: [...(d.yearly_data[y].water || EMPTY_MONTHLY)] as MonthlyValues,
              elec: [...(d.yearly_data[y].elec || EMPTY_MONTHLY)] as MonthlyValues,
            }
          : emptyYear();
      });
      setYearlyData(yd);
      setFloorArea(d.gross_floor_area || 0);
      setOpinion(d.professional_opinion || '');
    } else {
      const yd: EnergyYearlyData = {};
      years.forEach((y) => (yd[y] = emptyYear()));
      setYearlyData(yd);
    }
  }, [fetchedData, years]);

  const { mutate: saveMutate } = useMutation({
    mutationFn: postEnergyUsageAnalysis,
    onSuccess: () => {
      toast.success('에너지 사용량 분석 데이터가 저장되었습니다.');
      queryClient.invalidateQueries([
        QUERY_KEY.REPORT.ENERGY_USAGE_ANALYSIS,
        inspectionAreaId,
      ]);
      setEdit(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || '저장 실패');
    },
  });

  const handleSave = () => {
    saveMutate({
      inspectionAreaId,
      yearlyData,
      grossFloorArea: floorArea,
      professionalOpinion: opinion,
    });
  };

  const handleCancel = () => {
    if (fetchedData?.data) {
      const d = fetchedData.data;
      const yd: EnergyYearlyData = {};
      years.forEach((y) => {
        yd[y] = d.yearly_data?.[y]
          ? {
              gas: [...(d.yearly_data[y].gas || EMPTY_MONTHLY)] as MonthlyValues,
              water: [...(d.yearly_data[y].water || EMPTY_MONTHLY)] as MonthlyValues,
              elec: [...(d.yearly_data[y].elec || EMPTY_MONTHLY)] as MonthlyValues,
            }
          : emptyYear();
      });
      setYearlyData(yd);
      setFloorArea(d.gross_floor_area || 0);
      setOpinion(d.professional_opinion || '');
    }
    setEdit(false);
  };

  const handleCellChange = useCallback(
    (year: string, type: 'gas' | 'water' | 'elec', monthIdx: number, value: string) => {
      setYearlyData((prev) => {
        const updated = { ...prev };
        const yearData = { ...updated[year] };
        const arr = [...yearData[type]] as MonthlyValues;
        arr[monthIdx] = Number(value) || 0;
        yearData[type] = arr;
        updated[year] = yearData;
        return updated;
      });
    },
    []
  );

  const calculations = useMemo(() => {
    const result: Record<string, YearCalculation> = {};
    years.forEach((y) => {
      if (yearlyData[y]) {
        result[y] = calcYear(yearlyData[y], floorArea);
      }
    });
    return result;
  }, [yearlyData, floorArea, years]);

  const fmt = (n: number, decimals = 2) =>
    isNaN(n) || !isFinite(n) ? '-' : n.toLocaleString('ko-KR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <GreyBox width='58.125rem'>
      <S.Container>
        {/* Header - EXACT SAME FORMAT AS OTHERS */}
        <S.TopContainer>
          <Text fontSize='1.25rem' fontWeight={600}>
            에너지 사용량 분석
          </Text>
          <div>
            {isEdit && (
              <Button
                backgroundColor={Theme.colors.G_2}
                onClick={handleCancel}
              >
                취소하기
              </Button>
            )}
            <Button
              backgroundColor={isEdit ? Theme.colors.BLACK : Theme.colors.B_3}
              onClick={() => {
                if (isEdit) handleSave();
                else setEdit(true);
              }}
            >
              {isEdit ? '저장하기' : '수정하기'}
            </Button>
          </div>
        </S.TopContainer>

        {/* Floor Area input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem' }}>
          <Text fontSize='0.875rem' fontWeight={500}>연면적 (㎡)</Text>
          <S.MetaInput
            type='number'
            disabled={!isEdit}
            value={floorArea || ''}
            onChange={(e) => setFloorArea(Number(e.target.value) || 0)}
            placeholder='0'
          />
        </div>

        {/* The Matrix */}
        <S.Table>
          <colgroup>
            <S.MonthCol />
            {years.map((y) => (
              <React.Fragment key={`col-${y}`}>
                <S.DataCol />
                <S.DataCol />
                <S.DataCol />
              </React.Fragment>
            ))}
          </colgroup>
          <thead>
            <tr>
              <S.TableHeader rowSpan={2}>월</S.TableHeader>
              {years.map((y) => (
                <S.TableHeader key={y} colSpan={3}>
                  {y}년
                </S.TableHeader>
              ))}
            </tr>
            <tr>
              {years.map((y) => (
                <React.Fragment key={`header-${y}`}>
                  <S.TableHeader>가스(N㎥)</S.TableHeader>
                  <S.TableHeader>중온수(Gcal)</S.TableHeader>
                  <S.TableHeader>전기(kWh)</S.TableHeader>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <S.InfoContainer>
            {MONTHS.map((label, mIdx) => (
              <tr key={mIdx}>
                <td>{label}</td>
                {years.map((y) => (
                  <React.Fragment key={`${y}-${mIdx}`}>
                    {(['gas', 'water', 'elec'] as const).map((type) => (
                      <td key={`${y}-${type}-${mIdx}`}>
                        <S.CellInput
                          type={isEdit ? 'number' : 'text'}
                          disabled={!isEdit}
                          value={
                            isEdit
                              ? yearlyData[y]?.[type]?.[mIdx] || ''
                              : fmt(yearlyData[y]?.[type]?.[mIdx] || 0, 0)
                          }
                          onChange={(e) =>
                            handleCellChange(y, type, mIdx, e.target.value)
                          }
                        />
                      </td>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            ))}

            {/* Total Row */}
            <tr className="total-row">
              <td>합계</td>
              {years.map((y) => (
                <React.Fragment key={`total-${y}`}>
                  <td>{fmt(calculations[y]?.totals?.gas ?? 0, 0)}</td>
                  <td>{fmt(calculations[y]?.totals?.water ?? 0, 0)}</td>
                  <td>{fmt(calculations[y]?.totals?.elec ?? 0, 0)}</td>
                </React.Fragment>
              ))}
            </tr>
          </S.InfoContainer>

          <tbody>
            <tr>
              <S.TableHeader colSpan={10}>종합 분석 결과</S.TableHeader>
            </tr>
            <tr>
              <S.TableFooter colSpan={10} style={{ padding: '0.625rem' }}>
                <S.ResultSection>
                  {years.map((y) => {
                    const c = calculations[y];
                    if (!c) return null;
                    return (
                      <S.ResultCard key={y}>
                        <Text fontSize='0.875rem' fontWeight={700}>
                          {y}년
                        </Text>
                        <S.ResultLabel>TOE (석유환산톤)</S.ResultLabel>
                        <S.ResultValue>{fmt(c.toe.totalToe, 4)}</S.ResultValue>
                        <S.ResultSubText>
                          가스 {fmt(c.toe.gasToe, 4)} · 중온수 {fmt(c.toe.waterToe, 4)} · 전기 {fmt(c.toe.elecToe, 4)}
                        </S.ResultSubText>
                        <S.ResultLabel style={{ marginTop: '0.625rem' }}>
                          1차에너지 (kWh/㎡)
                        </S.ResultLabel>
                        <S.ResultValue>{fmt(c.primaryEnergy.total, 2)}</S.ResultValue>
                        <S.ResultSubText>
                          가스 {fmt(c.primaryEnergy.primaryGas, 2)} · 중온수 {fmt(c.primaryEnergy.primaryWater, 2)} · 전기 {fmt(c.primaryEnergy.primaryElec, 2)}
                        </S.ResultSubText>
                      </S.ResultCard>
                    );
                  })}
                </S.ResultSection>
              </S.TableFooter>
            </tr>
            <tr>
              <S.TableHeader colSpan={10}>전문가 의견</S.TableHeader>
            </tr>
            <tr>
              <S.TableFooter colSpan={10}>
                {isEdit ? (
                  <S.StyledTextarea
                    name='비고란'
                    value={opinion}
                    onChange={(e) => setOpinion(e.target.value)}
                    placeholder='전문가 의견을 입력하세요...'
                  />
                ) : (
                  opinion
                )}
              </S.TableFooter>
            </tr>
          </tbody>
        </S.Table>
      </S.Container>
    </GreyBox>
  );
};

export default EnergyUsageMatrix;
